import { Box } from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";

import { TableActions } from "../../components/v3";
import { formatBalance, getBalanceColor } from "../../utils";

/**
 * Route paths for the subcontractors module.
 * Single source of truth — reuse these instead of hard-coding `/subcontractors...`, `/contracts...` or
 * `/subcontractor...` strings.
 */
export const SUBCONTRACTORS_ROUTES = {
  root: "/subcontractors",
  addContract: "/contracts/add",
  editContract: "/contracts/edit/:id",
  details: "/subcontractor/:id",
  payment: "/subcontractor/:subcontractorId/payment/:paymentId",
  contract: "/subcontractor/:subcontractorId/contract/:contractId",
} as const;

export const SubcontractorTableColumns = [
  { field: "sl", headerName: "Sl No", flex: 0.4, sortable: false },
  {
    field: "name",
    headerName: "Name",
    flex: 1.5,
    sortable: true,
  },
  {
    field: "place",
    headerName: "place",
    flex: 1.5,
    sortable: false,
  },
  {
    field: "mobile",
    headerName: "mobile",
    flex: 1.5,
    sortable: false,
  },
  {
    field: "formattedBalance",
    headerName: "Balance",
    flex: 1.5,
    sortable: false,
    renderCell: (params) => {
      const value = params.value;
      const rawBalance = params.row.balance;
      return <Box sx={{ color: getBalanceColor(parseFloat(rawBalance)), display: "flex", alignItems: "center" }}>{value}</Box>;
    },
  },
];

export const ContractTableColumns = [
  { field: "sl", headerName: "Sl No", flex: 0.4, sortable: false },
  {
    field: "projectName",
    headerName: "Project Name",
    flex: 1.5,
    sortable: false,
  },
  {
    field: "totalAmount",
    headerName: "amount",
    flex: 1,
    sortable: false,
    renderCell: (params) => {
      const value = params.value;
      if (!value) return "-";
      return <span style={{ color: "red", display: "flex", alignItems: "center", gap: 4 }}>₹ {Number(value).toFixed(2)}</span>;
    },
  },
  {
    field: "totalPayment",
    headerName: "payment",
    flex: 1,
    sortable: false,
  },
  {
    field: "balance",
    headerName: "balance",
    flex: 1,
    sortable: false,
  },
];

const buildSubcontractorActionsColumn = (
  handleActions,
  hasEditContract,
  hasDeleteContract,
  hasEditTransaction,
  hasDeleteTransaction,
) => ({
  field: "actions",
  headerName: "Action",
  sortable: false,
  flex: 1,
  renderCell: (params) => {
    const { type } = params.row;
    const actions = [];

    if (type === "contract") {
      if (hasEditContract) {
        actions.push({ icon: Edit, type: "edit", label: "Edit", onClick: handleActions });
      }
      if (hasDeleteContract) {
        actions.push({ icon: Delete, type: "delete", label: "Delete", onClick: handleActions });
      }
    } else if (type === "expense") {
      if (hasEditTransaction) {
        actions.push({ icon: Edit, type: "edit", label: "Edit", onClick: handleActions });
      }
      if (hasDeleteTransaction) {
        actions.push({ icon: Delete, type: "delete", label: "Delete", onClick: handleActions });
      }
    }
    return <TableActions item={params} actions={actions} />;
  },
});

export const getLedgerTableColumns = (
  handleActions,
  hasEditContract,
  hasDeleteContract,
  hasEditTransaction,
  hasDeleteTransaction,
) => {
  const headers = [
    { field: "sl", headerName: "Sl No", flex: 0.5, sortable: false },
    { field: "date", headerName: "Date", flex: 0.8, sortable: false },
    { field: "projectName", headerName: "project name", flex: 1.5, sortable: false },
    { field: "description", headerName: "description", flex: 1.5, sortable: false },
    {
      field: "amount",
      headerName: "amount",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const value = params.value;
        if (!value) return "-";
        return <span style={{ color: "red", display: "flex", alignItems: "center", gap: 4 }}>₹ {Number(value).toFixed(2)}</span>;
      },
    },
    {
      field: "payment",
      headerName: "out",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const value = params.value;
        if (!value) return "-";
        return (
          <span style={{ color: "green", display: "flex", alignItems: "center", gap: 4 }}>₹ {Number(value).toFixed(2)}</span>
        );
      },
    },
    {
      field: "balance",
      headerName: "Balance",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const rawBalance = params.value;
        if (rawBalance === null || rawBalance === undefined || rawBalance === "") return "-";
        // Invert the stored balance for display, to match formatBalance and the rest of the app.
        const num = -(Number(rawBalance) || 0);
        const formatted = `${num < 0 ? "-" : ""}₹ ${Math.abs(num).toFixed(2)}`;
        return <Box sx={{ color: getBalanceColor(num), display: "flex", alignItems: "center" }}>{formatted}</Box>;
      },
    },
  ];

  if (hasEditContract || hasDeleteContract || hasEditTransaction || hasDeleteTransaction) {
    headers.push(
      buildSubcontractorActionsColumn(
        handleActions,
        hasEditContract,
        hasDeleteContract,
        hasEditTransaction,
        hasDeleteTransaction,
      ),
    );
  }

  return headers;
};

export const getStatementTableColumns = (
  handleActions,
  hasEditContract,
  hasDeleteContract,
  hasEditTransaction,
  hasDeleteTransaction,
  selectedFilter,
) => {
  const headers = [
    { field: "sl", headerName: "Sl No", flex: 0.5, sortable: false },
    { field: "date", headerName: "Date", flex: 0.8, sortable: true, sortingOrder: ["desc", "asc", null] },
    { field: "projectName", headerName: "project name", flex: 1.5, sortable: false },
    { field: "description", headerName: "description", flex: 1.5, sortable: false },
    {
      field: "amount",
      headerName: "amount",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const value = params.value;
        if (!value) return "-";
        return <span style={{ color: "red", display: "flex", alignItems: "center", gap: 4 }}>₹ {Number(value).toFixed(2)}</span>;
      },
    },
    {
      field: "payment",
      headerName: "out",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const value = params.value;
        if (!value) return "-";
        return (
          <span style={{ color: "green", display: "flex", alignItems: "center", gap: 4 }}>₹ {Number(value).toFixed(2)}</span>
        );
      },
    },
    // { field: "balance", headerName: "Balance", flex: 1 },
  ];

  if (hasEditContract || hasDeleteContract || hasEditTransaction || hasDeleteTransaction) {
    headers.push(
      buildSubcontractorActionsColumn(
        handleActions,
        hasEditContract,
        hasDeleteContract,
        hasEditTransaction,
        hasDeleteTransaction,
      ),
    );
  }

  return selectedFilter === "contract"
    ? headers.filter((col) => col.field !== "payment")
    : selectedFilter === "expense"
      ? headers.filter((col) => col.field !== "amount")
      : headers;
};
